import { generateFullTaxInvoiceHtml } from './quotationTemplateTokens'

type InvoiceData = Parameters<typeof generateFullTaxInvoiceHtml>[0]

/** What GET /payment-schedules/:id/milestones/:id/running-bill returns. */
export interface RunningBillPreview {
  isRunningBill: true
  /** Empty until the bill has been requested or issued. */
  number: string
  docId: string | null
  docStatus: string
  stage: {
    stageIndex: number
    stageCount: number
    stageName: string
    percent: number
    taxable: number
    cgst: number
    sgst: number
    igst: number
    tax: number
    additional: number
    total: number
    items: Array<{
      name: string; description: string; notes: string; unit: string; hsnSac: string
      quantity: number; rate: number; discountPercent: number; taxRate: number
      taxableValue: number; taxAmount: number; lineTotal: number
    }>
  }
  stages: Array<{
    index: number; name: string; percent: number; dueDate: string | null
    taxable: number; cgst: number; sgst: number; igst: number; tax: number; total: number
    invoiceNumber: string; amountReceived: number; paidAt?: string | null; status: string
  }>
  contract: { subtotal: number; taxTotal: number; total: number }
  amountReceived: number
  balanceDue: number
  gstMode: 'intra' | 'inter' | 'none'
  gstEnabled: boolean
  /** Header fields of the proforma (else quotation) the bill follows: company, client, terms, declaration, footer… */
  header?: Record<string, any>
}

/** The parts of the company profile a document header falls back to. */
export interface CompanyProfileLike {
  name?: string; legalName?: string; tagline?: string; industry?: string
  gstin?: string; gst?: string; pan?: string; phone?: string; email?: string; officialEmail?: string; website?: string
  logo?: string; logoUrl?: string; signatureUrl?: string
  registeredAddress?: { line1?: string; line2?: string; city?: string; state?: string; pincode?: string }
}

const fmtDate = (iso: string | null | undefined, fallback: string) =>
  iso ? new Date(iso).toLocaleDateString('en-IN') : fallback

const blank = (v: unknown) => v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0)

const lines = (v: unknown): string[] | undefined => {
  if (Array.isArray(v)) return v.map((x) => String(x ?? '').trim()).filter(Boolean)
  if (typeof v === 'string' && v.trim()) return v.split('\n').map((x) => x.trim()).filter(Boolean)
  return undefined
}

/**
 * The company header, client details, terms, declaration, signatory, footer and display options a
 * running bill prints — the same ones the proforma (and quotation) it follows prints. The company
 * profile fills whatever the proforma leaves out.
 */
export function headerFields(header: Record<string, any> = {}, company: CompanyProfileLike = {}, defaults: { declaration?: string } = {}): Partial<InvoiceData> {
  const h = header
  const addr = company.registeredAddress
  const registered = addr ? [addr.line1, addr.line2, addr.city, addr.state, addr.pincode].filter(Boolean).join(', ') : ''
  const termsList = lines(h.termsList) ?? lines(h.terms)
  const assumptionsList = lines(h.assumptionsList) ?? lines(h.assumptions)
  const out: Record<string, any> = {
    companyName: h.businessName || company.legalName || company.name,
    companyAddress: h.businessAddress || registered,
    companyTagline: h.businessTagline || h.businessTag || company.tagline || company.industry,
    companyGstin: h.businessGstin || company.gstin || company.gst,
    companyPan: h.businessPan || company.pan,
    companyPhone: h.businessPhone || company.phone,
    companyEmail: h.businessEmail || company.officialEmail || company.email,
    companyWeb: h.businessWeb || company.website,
    companyLogo: h.businessLogo || company.logoUrl || company.logo,
    signatureUrl: h.signatureUrl || company.signatureUrl,
    customerName: h.clientName,
    customerEmail: h.clientEmail,
    customerContact: h.clientPhone,
    customerAddress: h.clientAddress,
    customerGstin: h.clientGstin,
    customerState: h.clientState,
    placeOfSupply: h.placeOfSupply || h.clientState,
    contactPerson: h.contactPerson,
    contactDesignation: h.contactDesignation,
    referenceNo: h.poNumber || h.referenceNo,
    reverseCharge: h.reverseCharge === true || h.reverseCharge === 'Yes' ? 'Yes' : 'No',
    paymentTerms: h.paymentTerms,
    terms: termsList?.join('\n'),
    termsList,
    assumptions: assumptionsList?.join('\n'),
    assumptionsList,
    declaration: h.declaration || defaults.declaration,
    signatory: h.signatory,
    signatoryRole: h.signatoryRole,
    footerLeft: h.footerLeft,
    footerRight: h.footerRight,
    preparedBy: h.preparedBy,
    subject: h.subject,
    introText: h.introText,
    currency: h.currency,
    displayOptions: h.displayOptions,
    bankAccName: h.bankAccName,
    bankAccNo: h.bankAccNo,
    bankName: h.bankName,
    bankBranch: h.bankBranch,
    bankIfsc: h.bankIfsc,
    bankUpi: h.bankUpi,
    shippingEnabled: h.shippingEnabled,
    shippingName: h.shippingName,
    shippingAddress: h.shippingAddress,
    shippingCity: h.shippingCity,
    shippingState: h.shippingState,
    shippingPincode: h.shippingPincode,
  }
  for (const k of Object.keys(out)) if (blank(out[k])) delete out[k]
  return out as Partial<InvoiceData>
}

/** `base` wins where it has a value; the proforma's header fills the rest. */
function fillMissing<T extends Record<string, any>>(base: T, extra: Partial<T>): T {
  const merged: Record<string, any> = { ...base }
  for (const [k, v] of Object.entries(extra)) if (blank(merged[k])) merged[k] = v
  return merged as T
}

const round2 = (n: number) => Math.round(n * 100) / 100

/** What is still unpaid on every stage except this one (later stages, and any earlier stage not yet paid in full). */
export function otherStagesDue(rb: Pick<RunningBillPreview, 'stage' | 'stages'>): number {
  return round2(
    rb.stages
      .filter((s) => s.index !== rb.stage.stageIndex)
      .reduce((sum, s) => sum + Math.max(0, s.total - s.amountReceived), 0)
  )
}

/**
 * Turns the header details already gathered for a tax invoice (client, bank, logo…) into a running bill:
 * the stage's own lines and totals, the received amount recorded against the stage, and the schedule of
 * every stage with the exact amounts the server worked out.
 */
export function applyRunningBill(
  base: InvoiceData,
  rb: RunningBillPreview,
  context: { quotationNumber?: string; proformaNumber?: string; company?: CompanyProfileLike; defaultDeclaration?: string },
): InvoiceData {
  const { stage } = rb
  const withHeader = fillMissing(base, headerFields(rb.header, context.company, { declaration: context.defaultDeclaration }))
  const dueIso = rb.stages[stage.stageIndex]?.dueDate ?? null
  const due = fmtDate(dueIso, 'On signing')
  const sourceNotes = lines(rb.header?.notes) ?? []
  return {
    ...withHeader,
    documentNumber: rb.number || 'Assigned when requested',
    invoiceNumber: rb.number || 'Assigned when requested',
    dueDate: due,
    items: stage.items.map((it) => ({
      description: it.name,
      subDescription: it.notes,
      hsn: it.hsnSac,
      qty: it.quantity,
      unit: it.unit,
      rate: it.rate,
      disc: 0,
      gst: it.taxRate,
    })) as any,
    subtotal: stage.taxable,
    taxableAmount: stage.taxable,
    taxTotal: stage.tax,
    grandTotal: stage.total,
    total: stage.total,
    additionalCharges: stage.additional > 0 ? stage.additional : undefined,
    headerDiscount: 0,
    stageAmountsAreFinal: true,
    stagePercent: stage.percent,
    milestoneIndex: stage.stageIndex,
    milestoneName: stage.stageName,
    milestonePhaseLabel: stage.stageName,
    stageName: stage.stageName,
    contractTaxable: rb.contract.subtotal || undefined,
    contractTax: rb.contract.taxTotal || undefined,
    contractGrand: rb.contract.total || undefined,
    gstEnabled: rb.gstEnabled,
    gstMode: rb.gstMode,
    amountReceived: rb.amountReceived,
    // Balance due also carries whatever is unpaid on the other stages, so the client sees the whole contract.
    otherStagesDue: otherStagesDue(rb),
    paymentType: 'milestone_based',
    runningBill: {
      stageNumber: stage.stageIndex + 1,
      stageCount: stage.stageCount,
      stageName: stage.stageName,
      percent: stage.percent,
    },
    notes: [
      `Quotation Reference: ${context.quotationNumber || '—'}`,
      ...(context.proformaNumber ? [`Proforma Reference: ${context.proformaNumber}`] : []),
      `Running bill ${stage.stageIndex + 1} of ${stage.stageCount}: ${stage.stageName} (${stage.percent}% of total contract value)`,
      `Stage due date: ${due}`,
      ...sourceNotes,
    ],
    milestones: rb.stages.map((s) => ({
      name: s.name,
      percent: s.percent,
      due: fmtDate(s.dueDate, `Stage ${s.index + 1}`),
      invoiceNumber: s.invoiceNumber,
      proformaNumber: context.proformaNumber,
      netAmount: s.taxable,
      cgstAmount: s.cgst,
      sgstAmount: s.sgst,
      igstAmount: s.igst,
      taxAmount: s.tax,
      amountDue: s.total,
      amountReceived: s.amountReceived,
      paidAt: s.paidAt || undefined,
      status: s.status,
      exact: true,
    })) as any,
  }
}
